(() => {
  'use strict';

  const api = window.GroundingPriceTest;
  const config = JSON.parse(document.getElementById('grounding-price-test-config').textContent);
  const [control, challenger] = config.products;
  const handles = new Set(config.products.map((product) => product.handle));
  const cardSelector = [...handles].map((handle) => `[data-grounding-price-card="${handle}"]`).join(',');
  const sourceVariants = new Map(config.products.flatMap((product) => product.variants.map((variant) => [String(variant.id), variant])));
  api.hasVariant = (id) => sourceVariants.has(String(id));
  let selected;
  let selectedVariants;

  function mapVariantId(id) {
    const source = sourceVariants.get(String(id));
    if (!source) return id;
    const target = selectedVariants.get(source.title);
    if (!target) throw new Error(`Unpaired Grounding Hoodie variant: ${source.title}`);
    return target.id;
  }

  function mapUrl(value) {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin) return value;
    const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
    if (!match || !handles.has(match[1])) return value;
    url.pathname = url.pathname.replace(`/products/${match[1]}`, `/products/${selected.handle}`);
    if (url.searchParams.has('variant')) url.searchParams.set('variant', mapVariantId(url.searchParams.get('variant')));
    // A price assignment always uses the original layout, including shared layout-test preview links.
    if (selected === challenger && url.searchParams.get('view') === 'grounding-layout-b') url.searchParams.delete('view');
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function mapProduct(product) {
    if (!product || !handles.has(product.handle)) return product;
    const variants = product.variants.map((variant) => {
      const target = selectedVariants.get(variant.title);
      if (!target) throw new Error(`Unpaired Grounding Hoodie variant: ${variant.title}`);
      return { ...variant, ...target, compare_at_price: target.compareAtPrice };
    });
    return {
      ...product, id: selected.id, handle: selected.handle, url: selected.url,
      price: selected.price, price_min: selected.price, price_max: selected.price,
      priceMin: selected.price, compareAtMin: selected.compareAtPrice,
      compare_at_price: selected.compareAtPrice, compare_at_price_min: selected.compareAtPrice,
      compare_at_price_max: selected.compareAtPrice, available: variants.some((variant) => variant.available), variants,
    };
  }

  function updateCard(card) {
    for (const element of [card, ...card.querySelectorAll('[href], [data-url], [data-product-url]')]) {
      for (const attribute of ['href', 'data-url', 'data-product-url']) {
        const value = element.getAttribute(attribute);
        if (!value) continue;
        const mapped = mapUrl(value);
        if (mapped !== value) element.setAttribute(attribute, mapped);
      }
    }
    for (const input of card.querySelectorAll('input[name="id"]')) {
      input.value = mapVariantId(input.value);
    }
    for (const price of card.querySelectorAll('.price-item--sale, .price__regular .price-item--regular:not(s), .cn-july-price b')) {
      if (price.textContent !== selected.formattedPrice) price.textContent = selected.formattedPrice;
    }
    for (const price of card.querySelectorAll('s.price-item--regular, .cn-july-price > span')) {
      if (price.textContent !== selected.formattedCompareAtPrice) price.textContent = selected.formattedCompareAtPrice;
    }
  }

  function updateTree(root) {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.matches?.(cardSelector)) updateCard(root);
    root.querySelectorAll(cardSelector).forEach(updateCard);
    const card = root.closest?.(cardSelector);
    if (card && card !== root) updateCard(card);
    // Navigation, editorial links and recommendations all retain their existing tracking parameters.
    const links = root.matches?.('a[href]') ? [root, ...root.querySelectorAll('a[href]')] : root.querySelectorAll('a[href]');
    for (const link of links) {
      const value = link.getAttribute('href');
      const mapped = mapUrl(value);
      if (mapped !== value) link.setAttribute('href', mapped);
    }
  }

  async function start() {
    if (config.products.some((product) => !product.id || !product.variants.length)) {
      throw new Error('Both published Grounding Hoodie products must contain variants.');
    }
    const deadline = Date.now() + 8000;
    while (typeof window.shoplift?.isHypothesisActive !== 'function') {
      if (Date.now() >= deadline) throw new Error('ShopLift did not become ready.');
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    const active = await window.shoplift.isHypothesisActive(config.hypothesisId);
    if (typeof active !== 'boolean') throw new Error('ShopLift returned an invalid assignment.');
    // false includes excluded/inactive visitors; it is never recorded here as an enrolled control.
    selected = active ? challenger : control;
    selectedVariants = new Map(selected.variants.map((variant) => [variant.title, variant]));
    if (selectedVariants.size !== control.variants.length || control.variants.some((variant) => !selectedVariants.has(variant.title))) {
      throw new Error('Grounding Hoodie color/size pairs differ between products.');
    }
    Object.assign(api, { mapVariantId, mapUrl, mapProduct, product: selected });
    if (handles.has(config.currentHandle)) {
      const destination = mapUrl(`${location.pathname}${location.search}${location.hash}`);
      if (destination !== `${location.pathname}${location.search}${location.hash}`) {
        location.replace(destination);
        return;
      }
    }
    updateTree(document);
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'attributes') updateTree(record.target);
        else record.addedNodes.forEach(updateTree);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'data-url', 'data-product-url'] });
    api.resolve(api);
    delete api.resolve;
    delete api.reject;
    document.documentElement.classList.remove('grounding-price-pending');
  }

  start().catch((error) => {
    api.reject(error);
    document.documentElement.classList.replace('grounding-price-pending', 'grounding-price-error');
    for (const root of document.querySelectorAll(`${cardSelector}, [data-grounding-price-surface], body.grounding-hoodie main, body.grounding-hoodie-2 main`)) root.inert = true;
    const message = document.createElement('p');
    message.setAttribute('role', 'alert');
    message.textContent = 'Product pricing could not load. Please refresh this page to continue.';
    document.querySelector('main')?.before(message);
    console.error('[Grounding price test]', error);
  });
})();
